import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Send, 
  Brain, 
  Clock, 
  FileText, 
  TrendingUp, 
  LogOut,
  MessageCircle,
  Bot,
  User,
  Copy
} from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'

// Función para formatear el texto de respuesta del bot
const formatBotResponse = (text) => {
  if (!text) return null
  
  // Dividir el texto en párrafos basado en dobles saltos de línea
  let paragraphs = text.split(/\n\s*\n/)
  
  // Si no hay párrafos separados, dividir por saltos de línea simples
  if (paragraphs.length === 1) {
    paragraphs = text.split('\n')
  }
  
  return paragraphs.map((paragraph, index) => {
    // Limpiar espacios extra
    const cleanParagraph = paragraph.trim()
    if (!cleanParagraph) return null
    
         // Detectar si es una línea con numeración - mantener el número original
     if (/^\d+\.\s/.test(cleanParagraph)) {
       const numberMatch = cleanParagraph.match(/^(\d+)\.\s(.*)$/)
       if (numberMatch) {
         const [, number, content] = numberMatch
         return (
           <div key={index} className="mb-2 flex">
             <span className="font-medium text-blue-600 mr-2 flex-shrink-0">{number}.</span>
             <span className="flex-1">{formatInlineText(content.trim())}</span>
           </div>
         )
       }
     }
    
    // Detectar listas con viñetas (-, *, •)
    if (/^[-*•]\s/.test(cleanParagraph)) {
      const items = cleanParagraph.split(/(?=[-*•]\s)/).filter(item => item.trim())
      return (
        <ul key={index} className="list-disc list-inside space-y-2 mb-4">
          {items.map((item, itemIndex) => {
            const cleanItem = item.replace(/^[-*•]\s*/, '').trim()
            return (
              <li key={itemIndex} className="leading-relaxed">
                {formatInlineText(cleanItem)}
              </li>
            )
          })}
        </ul>
      )
    }
    
    // Detectar encabezados (texto seguido de dos puntos al final)
    if (/^.+:$/.test(cleanParagraph) && cleanParagraph.length < 100) {
      return (
        <h4 key={index} className="font-semibold text-gray-900 mb-2 mt-4">
          {formatInlineText(cleanParagraph)}
        </h4>
      )
    }
    
    // Párrafo normal
    return (
      <p key={index} className="mb-3 leading-relaxed">
        {formatInlineText(cleanParagraph)}
      </p>
    )
  }).filter(Boolean)
}

// Función para formatear texto inline (negritas, cursivas, etc.)
const formatInlineText = (text) => {
  // Formatear texto en negritas (**texto** o __texto__)
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
  formatted = formatted.replace(/__(.*?)__/g, '<strong class="font-semibold">$1</strong>')
  
  // Formatear texto en cursivas (*texto* o _texto_)
  formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
  formatted = formatted.replace(/_(.*?)_/g, '<em class="italic">$1</em>')
  
  // Resaltar artículos legales (Artículo XX)
  formatted = formatted.replace(/(\bArtículo\s+\d+[^\w])/gi, '<span class="font-medium text-blue-700 bg-blue-50 px-1 rounded">$1</span>')
  
  // Resaltar fracciones (fracción I, II, III, etc.)
  formatted = formatted.replace(/(\bfracción\s+[IVX]+[^\w])/gi, '<span class="font-medium text-purple-700">$1</span>')
  
  // Resaltar porcentajes
  formatted = formatted.replace(/(\b\d+(?:\.\d+)?%)/g, '<span class="font-medium text-green-700">$1</span>')
  
  // Resaltar montos en pesos
  formatted = formatted.replace(/(\$[\d,]+(?:\.\d{2})?)/g, '<span class="font-medium text-green-700">$1</span>')
  
  // Retornar el texto formateado como JSX
  return <span dangerouslySetInnerHTML={{ __html: formatted }} />
}

export default function RAGDemo({ onLogout }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '¡Hola! Soy el asistente fiscal de IQsit. Puedo ayudarte con consultas sobre legislación fiscal mexicana. ¿En qué puedo asistirte hoy?',
      timestamp: new Date(),
      confidence: null,
      sources: []
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [stats, setStats] = useState({
    totalQueries: 0,
    avgResponseTime: 0,
    avgConfidence: 0
  })
  
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sampleQuestions = [
    "¿Cuáles son las principales obligaciones fiscales de una persona física?",
    "¿Qué es el CFDI y para qué se utiliza?",
    "¿Cómo se calcula el ISR para personas físicas?",
    "¿Cuáles son las deducciones personales permitidas?",
    "¿Qué es el Régimen Simplificado de Confianza?"
  ]

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsLoading(true)

    try {
      const startTime = Date.now()
      
      // Aquí conectaremos con N8N en el futuro
      // Por ahora simulamos la respuesta del RAG
      const response = await axios.post('https://automation.iqsit.com/webhook/chat-simple', {
        question: message.trim(),
        user_id: `demo_${Date.now()}`
      }, {
        timeout: 30000
      })

      const responseTime = Date.now() - startTime
      const data = response.data

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.answer || 'Lo siento, no pude procesar tu consulta.',
        timestamp: new Date(),
        confidence: data.confidence || 0,
        sources: data.sources || [],
        responseTime: responseTime
      }

      setMessages(prev => [...prev, botMessage])
      
      // Actualizar estadísticas
      setStats(prev => ({
        totalQueries: prev.totalQueries + 1,
        avgResponseTime: Math.round((prev.avgResponseTime * prev.totalQueries + responseTime) / (prev.totalQueries + 1)),
        avgConfidence: Math.round(((prev.avgConfidence * prev.totalQueries + (data.confidence * 100)) / (prev.totalQueries + 1)) * 100) / 100
      }))

      toast.success('Respuesta generada exitosamente')

    } catch (error) {
      console.error('Error al enviar mensaje:', error)
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Lo siento, ocurrió un error al procesar tu consulta. Por favor, intenta nuevamente.',
        timestamp: new Date(),
        confidence: 0,
        sources: [],
        isError: true
      }

      setMessages(prev => [...prev, errorMessage])
      toast.error('Error al procesar la consulta')
    } finally {
      setIsLoading(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Texto copiado al portapapeles')
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-MX', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100'
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Link>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center mr-3">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">RAG Fiscal Mexicano</h1>
                  <p className="text-sm text-gray-500">Asistente Inteligente</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Sidebar con stats */}
        <div className="w-80 bg-white border-r p-6 flex-shrink-0">
          <div className="space-y-6">
            {/* Stats */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas de la Sesión</h3>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <MessageCircle className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Consultas</p>
                      <p className="text-xl font-bold text-gray-900">{stats.totalQueries}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Tiempo Promedio</p>
                      <p className="text-xl font-bold text-gray-900">{stats.avgResponseTime}ms</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Confianza Promedio</p>
                      <p className="text-xl font-bold text-gray-900">{stats.avgConfidence}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preguntas de ejemplo */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preguntas de Ejemplo</h3>
              <div className="space-y-2">
                {sampleQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(question)}
                    disabled={isLoading}
                    className="w-full text-left p-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <AnimatePresence>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-3xl ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                    <div className="flex items-start space-x-3">
                      {message.type === 'bot' && (
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className={`rounded-2xl px-4 py-3 ${
                          message.type === 'user' 
                            ? 'bg-blue-600 text-white' 
                            : message.isError 
                              ? 'bg-red-50 border border-red-200 text-red-800'
                              : 'bg-white border shadow-sm'
                        }`}>
                          <div className="text-sm leading-relaxed">
                            {message.type === 'bot' && !message.isError ? 
                              formatBotResponse(message.content) : 
                              <div className="whitespace-pre-wrap">{message.content}</div>
                            }
                          </div>
                          
                          {/* Metadata para respuestas del bot */}
                          {message.type === 'bot' && !message.isError && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                  <span>{formatTime(message.timestamp)}</span>
                                  {message.confidence !== null && (
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(message.confidence)}`}>
                                      {Math.round(message.confidence * 100)}% confianza
                                    </span>
                                  )}
                                  {message.responseTime && (
                                    <span>{message.responseTime}ms</span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => copyToClipboard(message.content)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    title="Copiar respuesta"
                                  >
                                    <Copy className="w-3 h-3 text-gray-400" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Sources */}
                              {message.sources && message.sources.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Fuentes consultadas:</p>
                                  <div className="space-y-1">
                                    {message.sources.slice(0, 3).map((source, index) => (
                                      <div key={index} className="flex items-center text-xs text-gray-500">
                                        <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                                        <span className="truncate">{source.title}</span>
                                        <span className="ml-2 px-1 py-0.5 bg-gray-100 rounded text-xs">
                                          {Math.round(source.score * 100)}%
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {message.type === 'user' && (
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {/* Loading indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-white border shadow-sm rounded-2xl px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">Procesando consulta...</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t bg-white p-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu consulta fiscal aquí..."
                  className="w-full resize-none border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-h-32"
                  rows={1}
                  disabled={isLoading}
                />
              </div>
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:cursor-not-allowed flex items-center"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 